import {
  ChangeEvent,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DataContext from "../../../context/DataContext";
import { useToolsRunTrackedWells } from "../../../hooks/useToolsRunTrackedWells";
import {
  COILineItemProps,
  COIRecordType,
  COIStatus,
  COIUploadSummaryProps,
} from "../../../interfaces/interfaces";
import {
  COIDraftOverride,
  COIDraftResolveDialog,
} from "./COIDraftResolveDialog";
import { COIStatusBadge } from "./COIStatusBadge";
import {
  calculateCOINewRuntime,
  formatCOIDate,
  formatCOIInteger,
} from "./coiFormatters";

type NamedOption = { id: number; name: string };
type ProductOption = NamedOption & { partNumber?: string };
type BusyAction = "preview" | "confirm" | null;
type StatusFilter = "" | COIStatus;

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: (result: COIUploadSummaryProps) => void;
}

const rowIdentity = (row: Pick<COILineItemProps, "recordType" | "sourceRowNumber">) =>
  `${row.recordType}:${row.sourceRowNumber}`;

const reasonLabel = (reason?: string | null) =>
  reason?.replaceAll("_", " ") || "";

export const COIUploadForm = ({ open, onClose, onUploaded }: Props) => {
  const { clientOptions, productOptions } = useContext(DataContext);
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const clients = clientOptions as NamedOption[];
  const products = productOptions as ProductOption[];
  const abortRef = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [client, setClient] = useState<NamedOption | null>(null);
  const [draft, setDraft] = useState<COIUploadSummaryProps | null>(null);
  const [overrides, setOverrides] = useState<COIDraftOverride[]>([]);
  const [editingRow, setEditingRow] = useState<COILineItemProps | null>(null);
  const [recordType, setRecordType] = useState<COIRecordType>("inventory");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [busy, setBusy] = useState<BusyAction>(null);
  const [error, setError] = useState("");

  const visibleRows = useMemo(() => {
    const rows = (draft?.rows || []).filter(
      (row) => row.recordType === recordType,
    );
    return statusFilter
      ? rows.filter((row) => row.status === statusFilter)
      : rows;
  }, [draft?.rows, recordType, statusFilter]);

  const activeSummary = useMemo(() => {
    if (!draft) return null;
    const summary = { total: 0, matched: 0, needsReview: 0, error: 0 };
    draft.rows.forEach((row) => {
      if (row.recordType !== recordType) return;
      summary.total += 1;
      if (row.status === "matched") summary.matched += 1;
      else if (row.status === "needs_review") summary.needsReview += 1;
      else summary.error += 1;
    });
    return summary;
  }, [draft, recordType]);
  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setFile(null);
    setClient(null);
    setDraft(null);
    setOverrides([]);
    setEditingRow(null);
    setRecordType("inventory");
    setStatusFilter("");
    setBusy(null);
    setError("");
  };
  const cancel = () => {
    reset();
    onClose();
  };
  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null);
    setDraft(null);
    setOverrides([]);
    setError("");
  };
  const chooseClient = (value: NamedOption | null) => {
    setClient(value);
    setDraft(null);
    setOverrides([]);
    setError("");
    if (value) fetchDataWells(value.id);
  };

  const postWorkbook = async (
    endpoint: string,
    extra?: Record<string, string>,
  ) => {
    if (!file || !client) throw new Error("Select a client and workbook");
    const controller = new AbortController();
    abortRef.current = controller;
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("clientId", String(client.id));
    Object.entries(extra || {}).forEach(([key, value]) =>
      formData.append(key, value),
    );
    const response = await fetch(
      String(process.env.REACT_APP_DEV_API) + endpoint,
      {
        method: "POST",
        body: formData,
        signal: controller.signal,
        headers: { Authorization: "Bearer " + localStorage.getItem("info") },
      },
    );
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || "The workbook could not be processed");
    }
    return result.data as COIUploadSummaryProps;
  };

  const runRequest = async (
    action: Exclude<BusyAction, null>,
    request: () => Promise<COIUploadSummaryProps>,
  ) => {
    setBusy(action);
    setError("");
    try {
      return await request();
    } catch (requestError) {
      if (
        requestError instanceof DOMException &&
        requestError.name === "AbortError"
      ) return null;
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The workbook could not be processed",
      );
      return null;
    } finally {
      setBusy(null);
      abortRef.current = null;
    }
  };

  const preview = async () => {
    const result = await runRequest("preview", () =>
      postWorkbook("/coi-preview"),
    );
    if (result) setDraft(result);
  };
  const confirm = async () => {
    const result = await runRequest("confirm", () =>
      postWorkbook("/coi-upload", {
        overrides: JSON.stringify(overrides),
      }),
    );
    if (!result) return;
    reset();
    onClose();
    onUploaded(result);
  };

  const saveDraftRow = (
    override: COIDraftOverride,
    updatedRow: COILineItemProps,
  ) => {
    const samePartRows = (draft?.rows || []).filter(
      (row) =>
        row.partNumber.trim().toLowerCase() ===
          updatedRow.partNumber.trim().toLowerCase(),
    );
    setOverrides((current) => {
      const affected = new Set(samePartRows.map(rowIdentity));
      const next = current.filter(
        (item) =>
          !affected.has(
            `${item.recordType}:${item.sourceRowNumber}`,
          ),
      );
      samePartRows.forEach((row) => {
        const isEditedRow = rowIdentity(row) === rowIdentity(updatedRow);
        next.push(
          isEditedRow
            ? override
            : {
                recordType: row.recordType,
                sourceRowNumber: Number(row.sourceRowNumber),
                productId: override.productId,
                createProduct: override.createProduct,
              },
        );
      });
      return next;
    });
    setDraft((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((row) =>
              samePartRows.some(
                (candidate) => rowIdentity(candidate) === rowIdentity(row),
              )
                ? {
                    ...row,
                    productId: updatedRow.productId,
                    product: updatedRow.product,
                    status: "matched",
                    reviewReason: null,
                  }
                : row,
            ),
          }
        : current,
    );
    setEditingRow(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={cancel}
        fullWidth
        maxWidth={draft ? "xl" : "md"}
      >
        <DialogTitle sx={{ fontWeight: 850, pb: 1 }}>
          {draft
            ? "Review Customer Owned Inventory draft"
            : "Import Customer Owned Inventory"}
        </DialogTitle>
        <DialogContent>
          {busy ? <LinearProgress sx={{ mb: 2, borderRadius: 2 }} /> : null}
          {!draft ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                The confirmed workbook replaces the selected client’s current
                Inventory and Reinstall lists. Nothing changes until confirmation.
              </Typography>
              <Autocomplete
                options={clients}
                value={client}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => chooseClient(value)}
                disabled={Boolean(busy)}
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Workbook client"
                    placeholder="Search clients"
                    helperText="Every workbook row belongs to this client."
                    sx={{ mb: 2.5 }}
                  />
                )}
              />
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                  cursor: busy ? "default" : "pointer",
                  border: "1.5px dashed #C9C7BE",
                  borderRadius: 2.5,
                  bgcolor: "#FAFAF7",
                  "&:hover": { borderColor: "#ED8B00", bgcolor: "#FFF9EE" },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#FFF0D5",
                    color: "#B86700",
                  }}
                >
                  {file ? <InsertDriveFileOutlinedIcon /> : <CloudUploadRoundedIcon />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={750} noWrap>
                    {file?.name || "Choose an Excel file"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    .xlsx only · Inventory and Reinstall must be the first two sheets
                  </Typography>
                </Box>
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={selectFile}
                  disabled={Boolean(busy)}
                />
              </Box>
            </>
          ) : (
            <>
              <Tabs
                value={recordType}
                onChange={(_, value: COIRecordType) => {
                  setRecordType(value);
                  setStatusFilter("");
                }}
                sx={{
                  mb: 2,
                  borderBottom: "1px solid #E7E5DE",
                  "& .MuiTabs-indicator": { bgcolor: "#ED8B00", height: 3 },
                }}
              >
                <Tab
                  value="inventory"
                  label={`Inventory (${draft.summary.inventory.total})`}
                />
                <Tab
                  value="reinstall"
                  label={`Reinstall (${draft.summary.reinstall.total})`}
                />
              </Tabs>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                sx={{ mb: 2 }}
              >
                {[
                  ["Rows", activeSummary?.total],
                  ["Matched", activeSummary?.matched],
                  ["Review", activeSummary?.needsReview],
                  ["Errors", activeSummary?.error],
                ].map(([label, value]) => (
                  <Paper
                    key={String(label)}
                    variant="outlined"
                    sx={{ flex: 1, p: 1.35, bgcolor: "#FAFAF7" }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {label}
                    </Typography>
                    <Typography variant="h5" fontWeight={850}>{value}</Typography>
                  </Paper>
                ))}
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1.25,
                }}
              >
                <Box>
                  <Typography fontWeight={800}>{client?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Source fields are read-only; product and well matches can be corrected.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={statusFilter}
                      displayEmpty
                      onChange={(event) =>
                        setStatusFilter(event.target.value as StatusFilter)
                      }
                    >
                      <MenuItem value="">All rows</MenuItem>
                      <MenuItem value="matched">Matched</MenuItem>
                      <MenuItem value="needs_review">Needs review</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    color="inherit"
                    disabled={Boolean(busy)}
                    onClick={() => {
                      setDraft(null);
                      setOverrides([]);
                    }}
                  >
                    Change file or client
                  </Button>
                </Stack>
              </Box>
              <TableContainer
                sx={{
                  maxHeight: "56vh",
                  border: "1px solid #E8E6DE",
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>P/N / description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Install</TableCell>
                      <TableCell>Pull</TableCell>
                      <TableCell>{recordType === "inventory" ? "Runtime" : "Reinstall / new runtime"}</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!visibleRows.length ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                          <Typography variant="body2" color="text.secondary">
                            No rows match this draft filter.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {visibleRows.map((row) => (
                      <TableRow key={rowIdentity(row)} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800}>{row.partNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.equipmentDescription || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.equipmentCategory || "—"}</TableCell>
                        <TableCell>{formatCOIInteger(row.quantity)}</TableCell>
                        <TableCell>{formatCOIDate(row.installDate)}</TableCell>
                        <TableCell>{formatCOIDate(row.pullDate)}</TableCell>
                        <TableCell>
                          {recordType === "inventory"
                            ? formatCOIInteger(row.runtimeDays)
                            : `${formatCOIDate(row.reinstallDate)} · ${formatCOIInteger(calculateCOINewRuntime(row.reinstallDate))} days`}
                        </TableCell>
                        <TableCell>{row.well?.name || row.fromWellRaw || "—"}</TableCell>
                        <TableCell>{row.toText || "—"}</TableCell>
                        <TableCell>
                          <COIStatusBadge status={row.status} />
                          {row.status !== "matched" ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5, textTransform: "capitalize" }}
                            >
                              {reasonLabel(row.errorReason || row.reviewReason)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditRoundedIcon />}
                            disabled={row.status === "error" || Boolean(busy)}
                            onClick={() => setEditingRow(row)}
                          >
                            Edit match
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {activeSummary?.error ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {activeSummary.error} invalid source row(s) will be skipped.
                  Correct them in the workbook and upload again if they are required.
                </Alert>
              ) : null}
            </>
          )}
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={cancel} color="inherit">
            {busy ? "Cancel process" : "Cancel"}
          </Button>
          {!draft ? (
            <Button
              variant="contained"
              onClick={preview}
              disabled={!file || !client || Boolean(busy)}
              startIcon={<CloudUploadRoundedIcon />}
              sx={{ bgcolor: "#ED8B00", "&:hover": { bgcolor: "#C97500" } }}
            >
              Preview workbook
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={confirm}
              disabled={Boolean(busy)}
            >
              Replace current inventory
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <COIDraftResolveDialog
        row={editingRow}
        clientName={client?.name || ""}
        wells={wellOptions}
        products={products}
        onClose={() => setEditingRow(null)}
        onSave={saveDraftRow}
      />
    </>
  );
};
